import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Ticket, Search, RefreshCw } from 'lucide-react';
import { apiFetch } from '../../utils/apiClient';

// 고객관리 탭(Customer.js)과 동일한 이유로 30개씩 페이징한다 - 카드 대신
// 한 줄짜리 행이라 여유가 있지만, 통일성을 위해 같은 값을 쓴다.
const PAGE_SIZE = 30;

// used_date는 "YYYY-MM-DD HH:MM" 형식(서버 로컬 타임)으로 온다.
// CustomerCard.js의 formatVisitDateTime과 동일한 로직 - 올해 사용이면
// 연도를 생략하고, 작년 이전이면 연도를 붙인다.
const formatUsedDateTime = (dateTimeStr) => {
  if (!dateTimeStr) return '';
  const [datePart, timePart] = dateTimeStr.split(' ');
  const [year, month, day] = (datePart || '').split('-');
  if (!year || !month || !day) return dateTimeStr;
  const isThisYear = parseInt(year, 10) === new Date().getFullYear();
  const datePrefix = isThisYear
    ? `${parseInt(month, 10)}/${parseInt(day, 10)}`
    : `${year}/${parseInt(month, 10)}/${parseInt(day, 10)}`;
  return timePart ? `${datePrefix} ${timePart}` : datePrefix;
};

const isThisMonth = (dateTimeStr) => {
  const [year, month] = (dateTimeStr || '').split('-');
  const now = new Date();
  return parseInt(year, 10) === now.getFullYear() && parseInt(month, 10) === now.getMonth() + 1;
};

const CouponHistory = ({ storeInfo }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const listTopRef = useRef(null);

  const fetchHistory = useCallback(async () => {
    if (!storeInfo?.id) return;
    setLoading(true);
    setError('');
    try {
      const response = await apiFetch(
        `/api/customers/coupon-history?store_id=${encodeURIComponent(storeInfo.id)}`
      );
      const result = await response.json();
      if (result.success) {
        setHistory(result.data || []);
      } else {
        setError(result.message || '쿠폰 사용 내역을 불러오지 못했습니다.');
      }
    } catch (err) {
      setError('네트워크 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [storeInfo]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const filtered = history.filter((h) => h.phone_short.includes(searchTerm));

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  useEffect(() => {
    listTopRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [currentPage]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const monthlyCount = history.filter((h) => isThisMonth(h.used_date)).length;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-2xl font-bold text-gray-900">쿠폰 사용 내역</h2>
        <div className="flex items-center gap-2">
          <div className="relative w-full sm:w-72">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="전화번호 검색 (끝자리 4자리)"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 w-full text-sm"
            />
          </div>
          <button
            type="button"
            onClick={fetchHistory}
            disabled={loading}
            title="목록 새로고침"
            className="shrink-0 p-2 border border-gray-300 rounded-lg text-gray-500 hover:bg-gray-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 text-red-600 text-sm rounded-lg p-3">{error}</div>
      )}

      <div className="rounded-xl border border-rose-100 bg-rose-50 shadow-sm p-4 flex items-baseline gap-2">
        <span className="text-sm text-gray-600">이번 달 사용</span>
        <span className="text-xl font-bold text-rose-700 tabular-nums">{monthlyCount}</span>
        <span className="text-sm text-gray-600">건</span>
      </div>

      <div ref={listTopRef} className="bg-white rounded-lg shadow overflow-hidden">
        {loading && history.length === 0 ? (
          <div className="text-center py-12 text-gray-500 text-sm">불러오는 중...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-12 text-gray-500 text-sm">사용된 쿠폰이 없습니다.</div>
        ) : (
          paged.map((item) => (
            <div
              key={item.number}
              className="flex items-center gap-3.5 px-4 py-3 border-b border-gray-100 last:border-b-0"
            >
              <div className="shrink-0 w-8 h-8 rounded-lg bg-rose-500 flex items-center justify-center">
                <Ticket className="w-4 h-4 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-gray-900 truncate">{item.coupon_type || '쿠폰'}</div>
                <div className="text-xs text-gray-500">{item.phone_short}</div>
              </div>
              <div className="text-xs text-gray-500 tabular-nums whitespace-nowrap">
                {formatUsedDateTime(item.used_date)}
              </div>
            </div>
          ))
        )}
      </div>

      {!(loading && history.length === 0) && totalPages > 1 && (
        <div className="flex justify-center flex-wrap gap-1 pt-1">
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              className={`px-3 py-1.5 rounded text-sm ${
                currentPage === page ? 'bg-blue-500 text-white' : 'bg-white text-gray-600 border'
              }`}
            >
              {page}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default CouponHistory;
